import React from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faSpinner, faCopy, faFileAlt, faTasks, faClipboardList, faStethoscope, faSync } from '@fortawesome/free-solid-svg-icons';
import { Table, useAuth, UserAbility, StatusBadge } from '@medprompt/shared';
import TaskModal from '../tasks/TaskModal';
import ReferralModal from '../referrals/ReferralModal';
import ConsultationPreviewModal from '../components/ConsultationPreviewModal';
import HelpButton from '../help/HelpButton';
import { HELP_PAGE_IDS } from '../help/helpContent';
import styles from './PatientView.module.css';
import { formatNhsId, formatDateOfBirth, calculateAge, formatTimestamp } from './patientFormat';
import usePatientRecord from './usePatientRecord';
import usePatientActivity from './usePatientActivity';
import usePatientTables from './usePatientTables';

const PatientViewController = () => {
  const { nhsId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const hasConsultRead = user?.authorities?.includes(UserAbility.CONSULT_READ.key);
  const hasDocsRead = user?.authorities?.includes(UserAbility.DOCS_READ.key);
  const hasTaskRead = user?.authorities?.includes(UserAbility.TASK_READ.key);

  const record = usePatientRecord({ nhsId, location, navigate });
  const patientId = record.patientId;

  const activity = usePatientActivity({
    nhsId,
    patientId,
    patient: record.patient,
    navigate,
    hasDocsRead,
    hasConsultRead,
    hasTaskRead,
    addNotificationRef: record.addNotificationRef,
    patientService: record.patientService,
  });
  const tables = usePatientTables();

  const patientCodesData = record.patientRecord?.patientCodesData;

  if (record.loading) {
    return (
      <div className={styles['patient-view-container']}>
        <div className={styles['patient-view-loading']}>
          <FontAwesomeIcon icon={faSpinner} spin size="3x" />
          <p>Loading patient data...</p>
        </div>
      </div>
    );
  }

  if (!record.patient || !patientId) {
    return (
      <div className={styles['patient-view-container']}>
        <div className={styles['patient-view-error']}>
          <p>Patient not found</p>
          <button className={styles['header-button']} onClick={record.handleBack}>
            <FontAwesomeIcon icon={faArrowLeft} /> {record.backLabel}
          </button>
        </div>
      </div>
    );
  }

  const patient = record.patient;

  return (
    <div className={styles['patient-view-container']}>
      <div className={styles['patient-view-header']}>
        <button className={styles['header-button']} onClick={record.handleBack}>
          <FontAwesomeIcon icon={faArrowLeft} /> {record.backLabel}
        </button>
        <div className={styles['patient-view-title-container']}>
          <h1 className={styles['patient-view-title']}>
            {patient.title ? `${patient.title} ${patient.name}` : patient.name}
          </h1>
          {patient && (() => {
            const s = patient.status || 'ACTIVE';
            const map = {
              ACTIVE: { label: 'Active', colorFamily: 'green', tooltip: null },
              DEDUCTION_PENDING: { label: 'Deduction Pending', colorFamily: 'amber', tooltip: 'This patient is in the process of being deregistered from the practice.' },
              INACTIVE: { label: 'Inactive', colorFamily: 'red', tooltip: 'This patient has been deregistered from the practice.' },
            };
            const entry = map[s] || map['ACTIVE'];
            return (
              <StatusBadge
                label={entry.label}
                colorFamily={entry.colorFamily}
                size="small"
                tooltip={entry.tooltip}
              />
            );
          })()}
          <HelpButton pageId={HELP_PAGE_IDS.patientSearch} />
        </div>
      </div>

      <div className={styles['patient-view-content']}>
        <div className={styles['patient-view-demographics']}>
          <div className={styles['patient-details-section']}>
            <h3>Patient Details</h3>
            <div className={styles['patient-details-grid']}>
              <div className={styles['detail-row']}>
                <span className={styles['detail-label']}>NHS Number:</span>
                <div className={styles['detail-value-with-controls']}>
                  <span className={styles['detail-value']}>{formatNhsId(patient.nhsId) || <em>not found</em>}</span>
                  {patient.nhsId && patient.nhsId.length === 10 && (
                    <button className={styles['copy-button']} onClick={() => record.handleCopyToClipboard(patient.nhsId)} title="Copy NHS number">
                      <FontAwesomeIcon icon={faCopy} />
                    </button>
                  )}
                </div>
              </div>
              <div className={styles['detail-row']}>
                <span className={styles['detail-label']}>Date of Birth:</span>
                <span className={styles['detail-value']}>{formatDateOfBirth(patient.dateOfBirth) || <em>not found</em>}</span>
              </div>
              <div className={styles['detail-row']}>
                <span className={styles['detail-label']}>Age:</span>
                <span className={styles['detail-value']}>{calculateAge(patient.dateOfBirth) || <em>not found</em>}</span>
              </div>
              <div className={styles['detail-row']}>
                <span className={styles['detail-label']}>Sex:</span>
                <span className={styles['detail-value']}>{patient.sex || <em>not found</em>}</span>
              </div>
              <div className={styles['detail-row']}>
                <span className={styles['detail-label']}>Address:</span>
                <span className={styles['detail-value']}>{patient.address || <em>not found</em>}</span>
              </div>
              <div className={styles['detail-row']}>
                <span className={styles['detail-label']}>Phone:</span>
                <span className={styles['detail-value']}>{patient.phoneNumber || <em>not found</em>}</span>
              </div>
              <div className={styles['detail-row']}>
                <span className={styles['detail-label']}>Email:</span>
                <span className={styles['detail-value']}>{patient.email || <em>not found</em>}</span>
              </div>
            </div>
          </div>

          <div className={styles['patient-problems-section']}>
            <div className={styles['patient-problems-header']}>
              <h3>Problems</h3>
              {record.patientRecord && (
                <button
                  className={`${styles['patient-problems-refresh']}${record.refreshingPatientCodes ? ` ${styles['patient-problems-refresh-fetching']}` : ''}`}
                  onClick={record.handleRefreshPatientCodes}
                  disabled={record.refreshingPatientCodes}
                  title={record.refreshingPatientCodes ? 'Refreshing problems from EMIS' : 'Refresh problems from EMIS'}
                >
                  <FontAwesomeIcon icon={record.refreshingPatientCodes ? faSpinner : faSync} spin={record.refreshingPatientCodes} />
                  {record.refreshingPatientCodes && <span>Fetching...</span>}
                </button>
              )}
            </div>

            <div className={styles['problems-subsection']}>
              <h4>Active</h4>
              {(!patientCodesData?.activeCodes || patientCodesData.activeCodes.length === 0) ? (
                <div className={styles['no-data']}><em>No active problems found</em></div>
              ) : (
                <div className={styles['codes-list']}>
                  {Array.from(patientCodesData.activeCodes).map((code, index) => (
                    <div key={`active-${index}`} className={styles['code-item']}>
                      <span className={styles['code-term']}>{code.code?.terms?.[0] || code.code?.code || 'Unknown'}</span>
                      <span className={styles['code-date']}>{formatTimestamp(code.timestamp)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className={styles['problems-subsection']}>
              <h4>Significant Past</h4>
              {(!patientCodesData?.significantPastCodes || patientCodesData.significantPastCodes.length === 0) ? (
                <div className={styles['no-data']}><em>No significant past problems found</em></div>
              ) : (
                <div className={styles['codes-list']}>
                  {Array.from(patientCodesData.significantPastCodes).map((code, index) => (
                    <div key={`past-${index}`} className={styles['code-item']}>
                      <span className={styles['code-term']}>{code.code?.terms?.[0] || code.code?.code || 'Unknown'}</span>
                      <span className={styles['code-date']}>{formatTimestamp(code.timestamp)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className={styles['patient-view-tables']}>
          {hasDocsRead && (
          <div className={styles['patient-table-section']}>
            <div className={styles['table-section-header']}>
              <FontAwesomeIcon icon={faFileAlt} />
              <h3>Documents</h3>
              <span className={styles['table-count']}>{activity.documents.length}</span>
            </div>
            {activity.loadingDocs ? (
              <div className={styles['table-loading']}>
                <FontAwesomeIcon icon={faSpinner} spin /> Loading...
              </div>
            ) : (
              <Table
                id="PatientDocumentsTable"
                data={activity.documents}
                columns={tables.documentColumns}
                getRowKey={(d) => d.documentId}
                sortState={tables.docsSortState}
                onSortChange={tables.setDocsSortState}
                filterValues={tables.docsFilterValues}
                onFilterChange={tables.setDocsFilterValues}
                disabledFilters={tables.docsDisabledFilters}
                onDisabledFiltersChange={tables.setDocsDisabledFilters}
                paginationState={tables.docsPaginationState}
                onPaginationChange={tables.setDocsPaginationState}
                onFilteredSortedDataChange={activity.handleFilteredSortedDataChange}
                highlightedRows={tables.highlightedRows}
                onHighlightChange={tables.setHighlightedRows}
                onRowClick={(doc) => activity.handleDocumentClick(doc.documentId)}
                emptyMessage="No documents found for this patient."
                compact
              />
            )}
          </div>
          )}

          {hasConsultRead && (
            <div className={styles['patient-table-section']}>
              <div className={styles['table-section-header']}>
                <FontAwesomeIcon icon={faStethoscope} />
                <h3>Consultations</h3>
                <span className={styles['table-count']}>{activity.consultations.length}</span>
              </div>
              {activity.loadingConsultations ? (
                <div className={styles['table-loading']}>
                  <FontAwesomeIcon icon={faSpinner} spin /> Loading...
                </div>
              ) : (
                <Table
                  id="PatientConsultationsTable"
                  data={activity.consultations}
                  columns={tables.consultationColumns}
                  getRowKey={(c) => c.consultationId}
                  sortState={tables.consultsSortState}
                  onSortChange={tables.setConsultsSortState}
                  filterValues={tables.consultsFilterValues}
                  onFilterChange={tables.setConsultsFilterValues}
                  disabledFilters={tables.consultsDisabledFilters}
                  onDisabledFiltersChange={tables.setConsultsDisabledFilters}
                  paginationState={tables.consultsPaginationState}
                  onPaginationChange={tables.setConsultsPaginationState}
                  highlightedRows={tables.highlightedConsultRows}
                  onHighlightChange={tables.setHighlightedConsultRows}
                  onRowClick={activity.handleConsultationClick}
                  emptyMessage="No consultations found for this patient."
                  compact
                />
              )}
            </div>
          )}

          {hasTaskRead && (
          <div className={styles['patient-table-section']}>
            <div className={styles['table-section-header']}>
              <FontAwesomeIcon icon={faTasks} />
              <h3>Outcome Tasks</h3>
              <span className={styles['table-count']}>{activity.tasks.length}</span>
            </div>
            {activity.loadingTasks ? (
              <div className={styles['table-loading']}>
                <FontAwesomeIcon icon={faSpinner} spin /> Loading...
              </div>
            ) : (
              <Table
                id="PatientTasksTable"
                data={activity.tasks}
                columns={tables.taskColumns}
                getRowKey={(t) => t.taskId}
                sortState={tables.tasksSortState}
                onSortChange={tables.setTasksSortState}
                filterValues={tables.tasksFilterValues}
                onFilterChange={tables.setTasksFilterValues}
                disabledFilters={tables.tasksDisabledFilters}
                onDisabledFiltersChange={tables.setTasksDisabledFilters}
                paginationState={tables.tasksPaginationState}
                onPaginationChange={tables.setTasksPaginationState}
                highlightedRows={tables.highlightedTaskRows}
                onHighlightChange={tables.setHighlightedTaskRows}
                onRowClick={activity.handleTaskClick}
                emptyMessage="No tasks found for this patient."
                compact
              />
            )}
          </div>
          )}

          {hasDocsRead && (
          <div className={styles['patient-table-section']}>
            <div className={styles['table-section-header']}>
              <FontAwesomeIcon icon={faClipboardList} />
              <h3>Referrals</h3>
              <span className={styles['table-count']}>{activity.referrals.length}</span>
            </div>
            {activity.loadingReferrals ? (
              <div className={styles['table-loading']}>
                <FontAwesomeIcon icon={faSpinner} spin /> Loading...
              </div>
            ) : (
              <Table
                id="PatientReferralsTable"
                data={activity.referrals}
                columns={tables.referralColumns}
                getRowKey={(r) => r.referralId}
                sortState={tables.refsSortState}
                onSortChange={tables.setRefsSortState}
                filterValues={tables.refsFilterValues}
                onFilterChange={tables.setRefsFilterValues}
                disabledFilters={tables.refsDisabledFilters}
                onDisabledFiltersChange={tables.setRefsDisabledFilters}
                paginationState={tables.refsPaginationState}
                onPaginationChange={tables.setRefsPaginationState}
                highlightedRows={tables.highlightedRefRows}
                onHighlightChange={tables.setHighlightedRefRows}
                onRowClick={activity.handleReferralClick}
                emptyMessage="No referrals found for this patient."
                compact
              />
            )}
          </div>
          )}
        </div>
      </div>

      {activity.taskModalOpen && activity.selectedTask && (
        <TaskModal
          isOpen={activity.taskModalOpen}
          onClose={() => {
            const taskId = activity.selectedTask?.taskId;
            activity.closeTask();
            if (taskId) {
              tables.setHighlightedTaskRows(new Set([taskId]));
            }
          }}
          task={activity.selectedTask}
          isReadOnly={false}
          onSave={activity.handleTaskSave}
          onMarkComplete={activity.handleTaskMarkComplete}
          onDelete={activity.handleTaskDelete}
          loading={activity.taskModalLoading}
          activeUsers={activity.activeUsers}
          teams={activity.teams}
          getAssigneeName={activity.getAssigneeName}
          getViewerCount={undefined}
          getViewers={undefined}
        />
      )}

      {activity.referralModalOpen && activity.selectedReferral && (
        <ReferralModal
          isOpen={activity.referralModalOpen}
          onClose={() => {
            const referralId = activity.selectedReferral?.referralId;
            activity.closeReferral();
            if (referralId) {
              tables.setHighlightedRefRows(new Set([referralId]));
            }
          }}
          referral={activity.selectedReferral}
          onMarkComplete={activity.handleReferralMarkComplete}
          onUpdate={activity.handleReferralUpdate}
          onDelete={activity.handleReferralDelete}
          loading={activity.referralModalLoading}
          isReadOnly={false}
          activeUsers={activity.activeUsers}
          teams={activity.teams}
        />
      )}

      {activity.consultationModalOpen && activity.selectedConsultationId && (
        <ConsultationPreviewModal
          isOpen={activity.consultationModalOpen}
          onClose={() => {
            const consultationId = activity.selectedConsultationId;
            activity.closeConsultation();
            if (consultationId) {
              tables.setHighlightedConsultRows(new Set([consultationId]));
            }
          }}
          consultationId={activity.selectedConsultationId}
          fromPatientNhsId={nhsId}
          fromPatientId={patientId}
          fromPatientName={patient?.name}
        />
      )}
    </div>
  );
};

export default PatientViewController;
